import { Logger } from '@/Logger';

import { arraysEqual } from '../utility/miscFunctions';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { SpellingGroup } from './SpellCheckHandler';
import type { StyleCategory } from './StyleHandler';
import type { BrandFitCategory } from './WordListHandler/BrandFitHandler';

const LOG = Logger('TextUi');

export interface TextUiStateNext {
  view: TeoView;
}

export interface TextUiState {
  markers: string[];
  dimension: string | undefined;
  textProperty: string | undefined;
  dimensionDrawerOpen: boolean;
  title: string;
  brandFitCategory: BrandFitCategory | undefined;
  brandFitWordList: string | undefined;
  dictionaryCategory: string | undefined;
  dictionaryWordList: string | undefined;
  styleCategory: StyleCategory | undefined;
  styleTextProperty: string | undefined;
  spellCheckCategory: string | undefined;
  spellCheckGroup: SpellingGroup | undefined;
}

export type TextUiEvents = {
  [K in keyof TextUiState]: (value: TextUiState[K]) => void;
} & {
  changed: (state: TextUiState) => void;
};

export type TeoView =
  | 'new'
  | 'brand-scanner'
  | 'dictionary'
  | 'text-generator'
  | 'performance'
  | 'plain-language'
  | 'simple-language'
  | 'spellcheck'
  | 'style'
  | 'versions';

export class TextUi {
  public readonly state: TextUiState;
  public readonly events = new TeoEventTarget<TextUiEvents>();

  constructor(options: Partial<TextUiState> = {}) {
    this.state = {
      markers: [],
      dimension: '',
      textProperty: '',
      dimensionDrawerOpen: false,
      title: '',
      brandFitCategory: undefined,
      brandFitWordList: undefined,
      dictionaryCategory: undefined,
      dictionaryWordList: undefined,
      styleCategory: undefined,
      styleTextProperty: undefined,
      spellCheckCategory: undefined,
      spellCheckGroup: undefined,
      ...options,
    };
  }

  update<K extends keyof TextUiState>(
    property: K,
    value: TextUiState[K],
    skipCheck: boolean = false,
  ): void {
    LOG.dev('@.update', property, value, this.state[property], skipCheck);
    if (skipCheck || this.propertyIsDifferent(property, value)) {
      this.state[property] = value;
      LOG.dev('@.update TRIGGERED', property, value);
      this.events.triggerEvent(property, value);
      this.events.triggerEvent('changed', this.state);
    }
  }

  updateState(stateUpdate: Partial<TextUiState>, skipCheck = false) {
    const changed: (keyof TextUiState)[] = [];
    for (const property in stateUpdate) {
      if (
        skipCheck ||
        this.propertyIsDifferent(
          property as keyof TextUiState,
          stateUpdate[property as keyof TextUiState],
        )
      ) {
        changed.push(property as keyof TextUiState);
      }
    }
    LOG.dev('@.updateState CHANGED', changed);
    if (changed.length > 0) {
      Object.assign(this.state, stateUpdate);
      for (const property of changed) {
        this.events.triggerEvent(property, this.state[property]);
      }
      this.events.triggerEvent('changed', this.state);
    }
  }

  private propertyIsDifferent<K extends keyof TextUiState>(
    property: K,
    value: TextUiState[K],
  ): boolean {
    if (this.isArrayProperty(property)) {
      return !arraysEqual(this.state[property] as string[], value as string[]);
    } else {
      return this.state[property] !== value;
    }
  }

  private isArrayProperty<K extends keyof TextUiState>(property: K): boolean {
    return Array.isArray(this.state[property]);
  }
}
